// ============================================
// 🔐 관리자 패널 (Admin Panel)
// 계단 제안 관리 + 불편사항 관리
// ============================================
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, doc, updateDoc, deleteDoc, addDoc, query, orderBy, where } from 'firebase/firestore';

function AdminPanel({ userEmail }) {
  const [activeTab, setActiveTab] = useState('stairs'); // 'stairs', 'proposals', 'feedback'
  
  // 서울시 전체 구 목록
  const SEOUL_DISTRICTS = [
    '강남구', '강동구', '강북구', '강서구', '관악구',
    '광진구', '구로구', '금천구', '노원구', '도봉구',
    '동대문구', '동작구', '마포구', '서대문구', '서초구',
    '성동구', '성북구', '송파구', '양천구', '영등포구',
    '용산구', '은평구', '종로구', '중구', '중랑구'
  ];
  
  // 계단 제안
  const [proposals, setProposals] = useState([]);
  const [loadingProposals, setLoadingProposals] = useState(true);
  
  // 불편사항
  const [feedbacks, setFeedbacks] = useState([]);
  const [loadingFeedbacks, setLoadingFeedbacks] = useState(true);

  // ✨ 전체 계단 관리
  const [allStairs, setAllStairs] = useState([]);
  const [loadingStairs, setLoadingStairs] = useState(true);
  const [editingStair, setEditingStair] = useState(null);
  const [showAddStairForm, setShowAddStairForm] = useState(false);
  
  // 새 계단 추가 폼
  const [newStair, setNewStair] = useState({
    name: '',
    station: '',
    exitNumber: '',
    steps: '',
    district: '',
    lat: '',
    lng: '',
    photoUrl: ''
  });
  
  const [message, setMessage] = useState('');

  const ADMIN_EMAILS = ['moveoftoday.info@gmail.com'];

  useEffect(() => {
    if (ADMIN_EMAILS.includes(userEmail)) {
      loadProposals();
      loadFeedbacks();
      loadAllStairs();
    }
  }, [userEmail]);

  // --------------------------------------------
  // 🏗️ 전체 계단 로드
  // --------------------------------------------
  const loadAllStairs = async () => {
    setLoadingStairs(true);
    try {
      const q = query(collection(db, 'stairs'), orderBy('name', 'asc'));
      const snapshot = await getDocs(q);
      const stairsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllStairs(stairsList);
    } catch (error) {
      console.error('계단 로드 실패:', error);
    }
    setLoadingStairs(false);
  };

  // --------------------------------------------
  // ✏️ 계단 수정
  // --------------------------------------------
  const handleEditStair = (stair) => {
    setEditingStair(stair);
    setNewStair({
      name: stair.name,
      station: stair.station,
      exitNumber: stair.exitNumber,
      steps: stair.steps.toString(),
      district: stair.district,
      lat: stair.lat.toString(),
      lng: stair.lng.toString(),
      photoUrl: stair.photoUrl || ''
    });
  };

  const handleUpdateStair = async (e) => {
    e.preventDefault();

    try {
      await updateDoc(doc(db, 'stairs', editingStair.id), {
        name: newStair.name,
        station: newStair.station,
        exitNumber: newStair.exitNumber,
        steps: parseInt(newStair.steps),
        district: newStair.district,
        lat: parseFloat(newStair.lat),
        lng: parseFloat(newStair.lng),
        photoUrl: newStair.photoUrl || null,
        updatedAt: new Date(),
        updatedBy: userEmail
      });

      setMessage('✅ 계단 정보가 수정되었습니다');
      setEditingStair(null);
      setNewStair({
        name: '', station: '', exitNumber: '', steps: '',
        district: '', lat: '', lng: '', photoUrl: ''
      });
      await loadAllStairs();
    } catch (error) {
      setMessage('❌ 수정 실패: ' + error.message);
    }
  };

  const handleCancelEdit = () => {
    setEditingStair(null);
    setNewStair({
      name: '', station: '', exitNumber: '', steps: '',
      district: '', lat: '', lng: '', photoUrl: ''
    });
  };

  // --------------------------------------------
  // 🗑️ 계단 삭제
  // --------------------------------------------
  const handleDeleteStair = async (stairId, stairName) => {
    if (!window.confirm(`"${stairName}" 계단을 삭제하시겠습니까?\n\n⚠️ 이 계단과 관련된 모든 기록도 삭제됩니다!`)) {
      return;
    }

    try {
      // 계단 삭제
      await deleteDoc(doc(db, 'stairs', stairId));

      // 관련 기록 삭제 (선택사항)
      const recordsQuery = query(
        collection(db, 'user_records'),
        where('stairId', '==', stairId)
      );
      const recordsSnapshot = await getDocs(recordsQuery);
      
      const deletePromises = recordsSnapshot.docs.map(doc => 
        deleteDoc(doc.ref)
      );
      await Promise.all(deletePromises);

      setMessage(`✅ 계단과 관련 기록 ${recordsSnapshot.size}개가 삭제되었습니다`);
      await loadAllStairs();
    } catch (error) {
      setMessage('❌ 삭제 실패: ' + error.message);
    }
  };

  // --------------------------------------------
  // ➕ 새 계단 추가
  // --------------------------------------------
  const handleAddStair = async (e) => {
    e.preventDefault();

    try {
      await addDoc(collection(db, 'stairs'), {
        name: newStair.name,
        station: newStair.station,
        exitNumber: newStair.exitNumber,
        steps: parseInt(newStair.steps),
        district: newStair.district,
        lat: parseFloat(newStair.lat),
        lng: parseFloat(newStair.lng),
        photoUrl: newStair.photoUrl || null,
        createdAt: new Date(),
        addedBy: userEmail
      });

      setMessage('✅ 새 계단이 추가되었습니다!');
      setShowAddStairForm(false);
      setNewStair({
        name: '',
        station: '',
        exitNumber: '',
        steps: '',
        district: '',
        lat: '',
        lng: '',
        photoUrl: ''
      });
      await loadAllStairs();
    } catch (error) {
      setMessage('❌ 추가 실패: ' + error.message);
    }
  };

  // --------------------------------------------
  // 📋 계단 제안 로드
  // --------------------------------------------
  const loadProposals = async () => {
    console.log('📥 계단 제안 로딩 시작...');
    setLoadingProposals(true);
    try {
      const q = query(collection(db, 'stair_proposals'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const proposalsList = snapshot.docs.map(doc => {
        const data = doc.data();
        console.log('📋 제안:', { 
          id: doc.id, 
          stairName: data.stairName, 
          station: data.station, 
          steps: data.steps, 
          district: data.district,
          status: data.status 
        });
        return {
          id: doc.id,
          ...data
        };
      });
      setProposals(proposalsList);
      console.log('✅ 총', proposalsList.length, '개 제안 로드');
    } catch (error) {
      console.error('❌ 제안 로드 실패:', error);
    }
    setLoadingProposals(false);
  };

  // --------------------------------------------
  // 📮 불편사항 로드
  // --------------------------------------------
  const loadFeedbacks = async () => {
    setLoadingFeedbacks(true);
    try {
      const q = query(collection(db, 'feedback'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);
      const feedbacksList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFeedbacks(feedbacksList);
    } catch (error) {
      console.error('피드백 로드 실패:', error);
    }
    setLoadingFeedbacks(false);
  };

  // --------------------------------------------
  // ✅ 계단 제안 승인
  // --------------------------------------------
  const handleApprove = async (proposal) => {
    console.log('🔍 승인 시도:', proposal);
    console.log('📋 proposal 필드 확인:');
    console.log('  - stairName:', proposal.stairName);
    console.log('  - station:', proposal.station);
    console.log('  - steps:', proposal.steps, 'type:', typeof proposal.steps);
    console.log('  - district:', proposal.district, '(선택)');
    console.log('  - exitNumber:', proposal.exitNumber, '(선택)');
    
    // 필수 필드 검증 (stairName, station, steps만 필수)
    if (!proposal.stairName || !proposal.station || !proposal.steps) {
      console.error('❌ 필수 정보 누락!');
      console.error('  - stairName 없음:', !proposal.stairName);
      console.error('  - station 없음:', !proposal.station);
      console.error('  - steps 없음:', !proposal.steps);
      setMessage('❌ 필수 정보가 누락되었습니다. (역/건물명, 계단명, 계단 수는 필수입니다)');
      return;
    }

    if (!window.confirm(`"${proposal.stairName}" 계단을 승인하시겠습니까?`)) {
      return;
    }

    try {
      // 안전하게 필드 처리
      const stairData = {
        name: proposal.stairName || '',  // stairName → name으로 저장
        station: proposal.station || '',
        exitNumber: proposal.exitNumber || '',  // 선택사항
        steps: parseInt(proposal.steps) || 0,
        district: proposal.district || '',  // 선택사항
        lat: parseFloat(proposal.lat) || 0,
        lng: parseFloat(proposal.lng) || 0,
        photoUrl: proposal.photoUrl || null,
        createdAt: new Date(),
        approvedBy: userEmail,
        originalProposalId: proposal.id
      };
      
      console.log('💾 저장할 데이터:', stairData);

      // undefined 필드 제거
      Object.keys(stairData).forEach(key => {
        if (stairData[key] === undefined || stairData[key] === '') {
          delete stairData[key];
        }
      });
      
      console.log('✅ 정제된 데이터:', stairData);

      await addDoc(collection(db, 'stairs'), stairData);

      await updateDoc(doc(db, 'stair_proposals', proposal.id), {
        status: 'approved',
        approvedAt: new Date(),
        approvedBy: userEmail
      });

      setMessage('✅ 계단이 승인되었습니다!');
      console.log('🎉 승인 완료!');
      await loadProposals();
      await loadAllStairs(); // 계단 목록도 새로고침
    } catch (error) {
      setMessage('❌ 승인 실패: ' + error.message);
      console.error('❌ 승인 에러:', error);
    }
  };

  // --------------------------------------------
  // ❌ 계단 제안 거부
  // --------------------------------------------
  const handleReject = async (proposalId, reason) => {
    const rejectReason = reason || prompt('거부 사유를 입력하세요:');
    
    if (!rejectReason) return;

    try {
      await updateDoc(doc(db, 'stair_proposals', proposalId), {
        status: 'rejected',
        rejectedAt: new Date(),
        rejectedBy: userEmail,
        rejectReason: rejectReason
      });

      setMessage('✅ 제안이 거부되었습니다');
      await loadProposals();
    } catch (error) {
      setMessage('❌ 거부 실패: ' + error.message);
    }
  };

  // --------------------------------------------
  // 🗑️ 계단 제안 삭제
  // --------------------------------------------
  const handleDeleteProposal = async (proposalId) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'stair_proposals', proposalId));
      setMessage('✅ 제안이 삭제되었습니다');
      await loadProposals();
    } catch (error) {
      setMessage('❌ 삭제 실패: ' + error.message);
    }
  };

  // --------------------------------------------
  // 🔄 피드백 상태 변경
  // --------------------------------------------
  const handleFeedbackStatus = async (feedbackId, newStatus) => {
    try {
      await updateDoc(doc(db, 'feedback', feedbackId), {
        status: newStatus,
        updatedAt: new Date(),
        updatedBy: userEmail
      });

      setMessage('✅ 상태가 변경되었습니다');
      await loadFeedbacks();
    } catch (error) {
      setMessage('❌ 상태 변경 실패: ' + error.message);
    }
  };

  // --------------------------------------------
  // 🗑️ 피드백 삭제
  // --------------------------------------------
  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'feedback', feedbackId));
      setMessage('✅ 피드백이 삭제되었습니다');
      await loadFeedbacks();
    } catch (error) {
      setMessage('❌ 삭제 실패: ' + error.message);
    }
  };

  // --------------------------------------------
  // 📅 날짜 포맷
  // --------------------------------------------
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate();
    return date.toLocaleString('ko-KR');
  };

  // --------------------------------------------
  // 🎨 상태 배지 색상
  // --------------------------------------------
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#FFA726';
      case 'in_progress': return '#42A5F5';
      case 'resolved': return '#66BB6A';
      case 'approved': return '#66BB6A';
      case 'rejected': return '#EF5350';
      default: return '#BDBDBD';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return '대기중';
      case 'in_progress': return '진행중';
      case 'resolved': return '해결완료';
      case 'approved': return '승인됨';
      case 'rejected': return '거부됨';
      default: return status;
    }
  };

  // --------------------------------------------
  // 🚫 권한 체크
  // --------------------------------------------
  if (!ADMIN_EMAILS.includes(userEmail)) {
    return (
      <div style={styles.container}>
        <div style={styles.noAccess}>
          <h2>🔐 접근 권한 없음</h2>
          <p>관리자만 접근할 수 있습니다.</p>
        </div>
      </div>
    );
  }

  // --------------------------------------------
  // 🎨 UI 렌더링
  // --------------------------------------------
  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>🔐 관리자 패널</h1>
        <div style={styles.adminBadge}>관리자: {userEmail}</div>
      </div>

      {message && (
        <div style={styles.message}>{message}</div>
      )}

      {/* 탭 메뉴 */}
      <div style={styles.tabContainer}>
        <button
          onClick={() => setActiveTab('stairs')}
          style={{
            ...styles.tab,
            ...(activeTab === 'stairs' ? styles.tabActive : {})
          }}
        >
          🏗️ 계단 관리 ({allStairs.length})
        </button>
        <button
          onClick={() => setActiveTab('proposals')}
          style={{
            ...styles.tab,
            ...(activeTab === 'proposals' ? styles.tabActive : {})
          }}
        >
          📥 계단 제안 ({proposals.filter(p => p.status === 'pending').length})
        </button>
        <button
          onClick={() => setActiveTab('feedback')}
          style={{
            ...styles.tab,
            ...(activeTab === 'feedback' ? styles.tabActive : {})
          }}
        >
          📮 불편사항 ({feedbacks.filter(f => f.status === 'pending').length})
        </button>
      </div>

      {/* 계단 관리 탭 */}
      {activeTab === 'stairs' && (
        <div style={styles.content}>
          <div style={styles.statsBar}>
            <div style={styles.stat}>
              전체 계단: <strong>{allStairs.length}</strong>
            </div>
            <button
              onClick={() => setShowAddStairForm(!showAddStairForm)}
              style={styles.addButton}
            >
              ➕ 새 계단 추가
            </button>
          </div>

          {/* 새 계단 추가 폼 */}
          {showAddStairForm && !editingStair && (
            <div style={styles.addStairForm}>
              <h3 style={styles.formTitle}>🏗️ 새 계단 추가</h3>
              <form onSubmit={handleAddStair}>
                <div style={styles.formGrid}>
                  <input
                    type="text"
                    placeholder="계단 이름 *"
                    value={newStair.name}
                    onChange={(e) => setNewStair({...newStair, name: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <input
                    type="text"
                    placeholder="역/건물 *"
                    value={newStair.station}
                    onChange={(e) => setNewStair({...newStair, station: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <input
                    type="text"
                    placeholder="출구 번호 *"
                    value={newStair.exitNumber}
                    onChange={(e) => setNewStair({...newStair, exitNumber: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <input
                    type="number"
                    placeholder="계단 개수 *"
                    value={newStair.steps}
                    onChange={(e) => setNewStair({...newStair, steps: e.target.value})}
                    style={styles.input}
                    required
                    min="1"
                  />
                  <select
                    value={newStair.district}
                    onChange={(e) => setNewStair({...newStair, district: e.target.value})}
                    style={styles.input}
                    required
                  >
                    <option value="">지역 선택 *</option>
                    {SEOUL_DISTRICTS.map(district => (<option key={district} value={district}>{district}</option>))}
                    <option value="강서구">강서구</option>
                    <option value="마포구">마포구</option>
                    <option value="영등포구">영등포구</option>
                  </select>
                  <input
                    type="text"
                    placeholder="위도 (예: 37.5665)"
                    value={newStair.lat}
                    onChange={(e) => setNewStair({...newStair, lat: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <input
                    type="text"
                    placeholder="경도 (예: 126.9780)"
                    value={newStair.lng}
                    onChange={(e) => setNewStair({...newStair, lng: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <input
                    type="text"
                    placeholder="사진 URL (선택)"
                    value={newStair.photoUrl}
                    onChange={(e) => setNewStair({...newStair, photoUrl: e.target.value})}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formActions}>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddStairForm(false);
                      setNewStair({
                        name: '', station: '', exitNumber: '', steps: '',
                        district: '', lat: '', lng: '', photoUrl: ''
                      });
                    }}
                    style={styles.cancelButton}
                  >
                    취소
                  </button>
                  <button type="submit" style={styles.submitButton}>
                    추가하기
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* 계단 수정 폼 */}
          {editingStair && (
            <div style={styles.editStairForm}>
              <h3 style={styles.formTitle}>✏️ 계단 수정: {editingStair.name}</h3>
              <form onSubmit={handleUpdateStair}>
                <div style={styles.formGrid}>
                  <input
                    type="text"
                    placeholder="계단 이름 *"
                    value={newStair.name}
                    onChange={(e) => setNewStair({...newStair, name: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <input
                    type="text"
                    placeholder="역/건물 *"
                    value={newStair.station}
                    onChange={(e) => setNewStair({...newStair, station: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <input
                    type="text"
                    placeholder="출구 번호 *"
                    value={newStair.exitNumber}
                    onChange={(e) => setNewStair({...newStair, exitNumber: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <input
                    type="number"
                    placeholder="계단 개수 *"
                    value={newStair.steps}
                    onChange={(e) => setNewStair({...newStair, steps: e.target.value})}
                    style={styles.input}
                    required
                    min="1"
                  />
                  <select
                    value={newStair.district}
                    onChange={(e) => setNewStair({...newStair, district: e.target.value})}
                    style={styles.input}
                    required
                  >
                    <option value="">지역 선택 *</option>
                    {SEOUL_DISTRICTS.map(district => (<option key={district} value={district}>{district}</option>))}
                    <option value="강서구">강서구</option>
                    <option value="마포구">마포구</option>
                    <option value="영등포구">영등포구</option>
                  </select>
                  <input
                    type="text"
                    placeholder="위도 (예: 37.5665)"
                    value={newStair.lat}
                    onChange={(e) => setNewStair({...newStair, lat: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <input
                    type="text"
                    placeholder="경도 (예: 126.9780)"
                    value={newStair.lng}
                    onChange={(e) => setNewStair({...newStair, lng: e.target.value})}
                    style={styles.input}
                    required
                  />
                  <input
                    type="text"
                    placeholder="사진 URL (선택)"
                    value={newStair.photoUrl}
                    onChange={(e) => setNewStair({...newStair, photoUrl: e.target.value})}
                    style={styles.input}
                  />
                </div>
                <div style={styles.formActions}>
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    style={styles.cancelButton}
                  >
                    취소
                  </button>
                  <button type="submit" style={styles.updateButton}>
                    수정하기
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* 계단 목록 */}
          {loadingStairs ? (
            <div style={styles.loading}>로딩 중...</div>
          ) : allStairs.length === 0 ? (
            <div style={styles.empty}>등록된 계단이 없습니다</div>
          ) : (
            <div style={styles.list}>
              {allStairs.map(stair => (
                <div key={stair.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div style={styles.cardTitle}>
                      🏃 {stair.name}
                    </div>
                    <div style={styles.stairSteps}>
                      {stair.steps}개
                    </div>
                  </div>

                  <div style={styles.cardBody}>
                    <div style={styles.infoGrid}>
                      <div style={styles.infoItem}>
                        <strong>역/건물:</strong> {stair.station}
                      </div>
                      <div style={styles.infoItem}>
                        <strong>출구:</strong> {stair.exitNumber}
                      </div>
                      <div style={styles.infoItem}>
                        <strong>지역:</strong> {stair.district}
                      </div>
                      <div style={styles.infoItem}>
                        <strong>위치:</strong> {stair.lat}, {stair.lng}
                      </div>
                      {stair.addedBy && (
                        <div style={styles.infoItem}>
                          <strong>등록자:</strong> {stair.addedBy}
                        </div>
                      )}
                      {stair.updatedBy && (
                        <div style={styles.infoItem}>
                          <strong>수정자:</strong> {stair.updatedBy}
                        </div>
                      )}
                    </div>

                    {stair.photoUrl && (
                      <div style={styles.photoSection}>
                        <img 
                          src={stair.photoUrl} 
                          alt="계단 사진" 
                          style={styles.photo}
                        />
                      </div>
                    )}
                  </div>

                  <div style={styles.cardActions}>
                    <button
                      onClick={() => handleEditStair(stair)}
                      style={{...styles.actionButton, ...styles.editButton}}
                    >
                      ✏️ 수정
                    </button>
                    <button
                      onClick={() => handleDeleteStair(stair.id, stair.name)}
                      style={{...styles.actionButton, ...styles.deleteButton}}
                    >
                      🗑️ 삭제
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 계단 제안 탭 */}
      {activeTab === 'proposals' && (
        <div style={styles.content}>
          <div style={styles.statsBar}>
            <div style={styles.stat}>
              전체: <strong>{proposals.length}</strong>
            </div>
            <div style={styles.stat}>
              대기: <strong style={{ color: '#FFA726' }}>
                {proposals.filter(p => p.status === 'pending').length}
              </strong>
            </div>
            <div style={styles.stat}>
              승인: <strong style={{ color: '#66BB6A' }}>
                {proposals.filter(p => p.status === 'approved').length}
              </strong>
            </div>
            <div style={styles.stat}>
              거부: <strong style={{ color: '#EF5350' }}>
                {proposals.filter(p => p.status === 'rejected').length}
              </strong>
            </div>
          </div>

          {loadingProposals ? (
            <div style={styles.loading}>로딩 중...</div>
          ) : proposals.length === 0 ? (
            <div style={styles.empty}>제안된 계단이 없습니다</div>
          ) : (
            <div style={styles.list}>
              {proposals.map(proposal => (
                <div key={proposal.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div style={styles.cardTitle}>
                      🏃 {proposal.name}
                    </div>
                    <div style={{
                      ...styles.statusBadge,
                      backgroundColor: getStatusColor(proposal.status)
                    }}>
                      {getStatusText(proposal.status)}
                    </div>
                  </div>

                  <div style={styles.cardBody}>
                    {/* ⚠️ 데이터 검증 경고 (stairName, station, steps만 필수) */}
                    {(!proposal.stairName || !proposal.station || !proposal.steps || proposal.steps > 1000) && (
                      <div style={styles.warningBox}>
                        ⚠️ 이 제안은 데이터에 문제가 있습니다:
                        <ul style={{ margin: '5px 0', paddingLeft: '20px' }}>
                          {!proposal.stairName && <li>계단명 누락</li>}
                          {!proposal.station && <li>역/건물명 누락</li>}
                          {!proposal.steps && <li>계단 수 누락</li>}
                          {proposal.steps > 1000 && <li>계단 수 비정상 ({proposal.steps}개)</li>}
                        </ul>
                      </div>
                    )}

                    <div style={styles.infoGrid}>
                      <div style={styles.infoItem}>
                        <strong>역/건물:</strong> {proposal.station || '(없음)'}
                      </div>
                      <div style={styles.infoItem}>
                        <strong>출구:</strong> {proposal.exitNumber || '(없음)'}
                      </div>
                      <div style={styles.infoItem}>
                        <strong>계단 수:</strong> {proposal.steps ? `${proposal.steps}개` : '(없음)'}
                      </div>
                      <div style={styles.infoItem}>
                        <strong>지역:</strong> {proposal.district || '(없음)'}
                      </div>
                      <div style={styles.infoItem}>
                        <strong>제안자:</strong> {proposal.userEmail}
                      </div>
                      <div style={styles.infoItem}>
                        <strong>제안일:</strong> {formatDate(proposal.createdAt)}
                      </div>
                    </div>

                    {proposal.photoUrl && (
                      <div style={styles.photoSection}>
                        <img 
                          src={proposal.photoUrl} 
                          alt="계단 사진" 
                          style={styles.photo}
                        />
                      </div>
                    )}

                    {proposal.status === 'rejected' && proposal.rejectReason && (
                      <div style={styles.rejectReason}>
                        <strong>거부 사유:</strong> {proposal.rejectReason}
                      </div>
                    )}
                  </div>

                  <div style={styles.cardActions}>
                    {proposal.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleApprove(proposal)}
                          style={{
                            ...styles.actionButton, 
                            ...styles.approveButton,
                            ...(!proposal.stairName || !proposal.station || !proposal.steps || proposal.steps > 1000 ? styles.disabledButton : {})
                          }}
                          disabled={!proposal.stairName || !proposal.station || !proposal.steps || proposal.steps > 1000}
                          title={!proposal.stairName || !proposal.station || !proposal.steps || proposal.steps > 1000 ? '필수 데이터가 없거나 올바르지 않습니다' : ''}
                        >
                          ✅ 승인
                        </button>
                        <button
                          onClick={() => {
                            // 데이터 문제가 있으면 자동 거부 사유
                            if (!proposal.station || !proposal.steps || proposal.steps > 1000) {
                              const reasons = [];
                              if (!proposal.station) reasons.push('역/건물명 누락');
                              if (!proposal.steps) reasons.push('계단 수 누락');
                              if (proposal.steps > 1000) reasons.push('계단 수 비정상');
                              handleReject(proposal.id, reasons.join(', '));
                            } else {
                              handleReject(proposal.id);
                            }
                          }}
                          style={{...styles.actionButton, ...styles.rejectButton}}
                        >
                          ❌ 거부
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => handleDeleteProposal(proposal.id)}
                      style={{...styles.actionButton, ...styles.deleteButton}}
                    >
                      🗑️ 삭제
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 불편사항 탭 */}
      {activeTab === 'feedback' && (
        <div style={styles.content}>
          <div style={styles.statsBar}>
            <div style={styles.stat}>
              전체: <strong>{feedbacks.length}</strong>
            </div>
            <div style={styles.stat}>
              대기: <strong style={{ color: '#FFA726' }}>
                {feedbacks.filter(f => f.status === 'pending').length}
              </strong>
            </div>
            <div style={styles.stat}>
              진행중: <strong style={{ color: '#42A5F5' }}>
                {feedbacks.filter(f => f.status === 'in_progress').length}
              </strong>
            </div>
            <div style={styles.stat}>
              해결: <strong style={{ color: '#66BB6A' }}>
                {feedbacks.filter(f => f.status === 'resolved').length}
              </strong>
            </div>
          </div>

          {loadingFeedbacks ? (
            <div style={styles.loading}>로딩 중...</div>
          ) : feedbacks.length === 0 ? (
            <div style={styles.empty}>불편사항이 없습니다</div>
          ) : (
            <div style={styles.list}>
              {feedbacks.map(feedback => (
                <div key={feedback.id} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <div style={styles.cardTitle}>
                      📮 {feedback.title}
                    </div>
                    <div style={{
                      ...styles.statusBadge,
                      backgroundColor: getStatusColor(feedback.status)
                    }}>
                      {getStatusText(feedback.status)}
                    </div>
                  </div>

                  <div style={styles.cardBody}>
                    <div style={styles.feedbackContent}>
                      {feedback.content}
                    </div>

                    <div style={styles.infoGrid}>
                      <div style={styles.infoItem}>
                        <strong>작성자:</strong> {feedback.userEmail}
                      </div>
                      <div style={styles.infoItem}>
                        <strong>작성일:</strong> {formatDate(feedback.createdAt)}
                      </div>
                    </div>

                    {feedback.screenshots && feedback.screenshots.length > 0 && (
                      <div style={styles.screenshotsSection}>
                        <strong>스크린샷:</strong>
                        <div style={styles.screenshotsGrid}>
                          {feedback.screenshots.map((url, index) => (
                            <a 
                              key={index} 
                              href={url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              <img 
                                src={url} 
                                alt={`스크린샷 ${index + 1}`} 
                                style={styles.screenshot}
                              />
                            </a>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={styles.cardActions}>
                    <select
                      value={feedback.status}
                      onChange={(e) => handleFeedbackStatus(feedback.id, e.target.value)}
                      style={styles.statusSelect}
                    >
                      <option value="pending">대기중</option>
                      <option value="in_progress">진행중</option>
                      <option value="resolved">해결완료</option>
                    </select>
                    <button
                      onClick={() => handleDeleteFeedback(feedback.id)}
                      style={{...styles.actionButton, ...styles.deleteButton}}
                    >
                      🗑️ 삭제
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// 🎨 스타일
// ============================================
const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px'
  },
  header: {
    marginBottom: '30px'
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '10px'
  },
  adminBadge: {
    display: 'inline-block',
    padding: '8px 16px',
    backgroundColor: '#616161',
    color: 'white',
    borderRadius: '20px',
    fontSize: '14px'
  },
  message: {
    padding: '15px',
    marginBottom: '20px',
    backgroundColor: '#e8f5e9',
    borderRadius: '8px',
    textAlign: 'center',
    fontSize: '14px'
  },
  tabContainer: {
    display: 'flex',
    gap: '10px',
    marginBottom: '20px',
    borderBottom: '2px solid #e0e0e0'
  },
  tab: {
    flex: 1,
    padding: '15px 20px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '3px solid transparent',
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#666',
    cursor: 'pointer',
    transition: 'all 0.3s'
  },
  tabActive: {
    color: '#616161',
    borderBottom: '3px solid #616161'
  },
  content: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  statsBar: {
    display: 'flex',
    gap: '20px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    marginBottom: '20px'
  },
  stat: {
    fontSize: '14px',
    color: '#666'
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    color: '#666'
  },
  empty: {
    textAlign: 'center',
    padding: '40px',
    fontSize: '16px',
    color: '#999'
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  card: {
    border: '2px solid #e0e0e0',
    borderRadius: '10px',
    overflow: 'hidden',
    transition: 'box-shadow 0.3s',
    ':hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
    }
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 20px',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0'
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333'
  },
  statusBadge: {
    padding: '6px 12px',
    borderRadius: '15px',
    fontSize: '12px',
    fontWeight: 'bold',
    color: 'white'
  },
  cardBody: {
    padding: '20px'
  },
  warningBox: {
    backgroundColor: '#fff3cd',
    border: '2px solid #ffc107',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '15px',
    fontSize: '13px',
    color: '#856404'
  },
  infoGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '10px',
    marginBottom: '15px'
  },
  infoItem: {
    fontSize: '14px',
    color: '#666'
  },
  photoSection: {
    marginTop: '15px'
  },
  photo: {
    width: '100%',
    maxWidth: '400px',
    borderRadius: '8px',
    border: '2px solid #e0e0e0'
  },
  feedbackContent: {
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '8px',
    marginBottom: '15px',
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#333',
    whiteSpace: 'pre-wrap'
  },
  screenshotsSection: {
    marginTop: '15px'
  },
  screenshotsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '10px',
    marginTop: '10px'
  },
  screenshot: {
    width: '100%',
    height: '150px',
    objectFit: 'cover',
    borderRadius: '8px',
    border: '2px solid #e0e0e0',
    cursor: 'pointer',
    transition: 'transform 0.3s'
  },
  rejectReason: {
    padding: '15px',
    backgroundColor: '#ffebee',
    borderRadius: '8px',
    marginTop: '15px',
    fontSize: '14px',
    color: '#c62828'
  },
  cardActions: {
    display: 'flex',
    gap: '10px',
    padding: '15px 20px',
    backgroundColor: '#f8f9fa',
    borderTop: '1px solid #e0e0e0'
  },
  actionButton: {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'opacity 0.3s'
  },
  approveButton: {
    backgroundColor: '#66BB6A',
    color: 'white'
  },
  rejectButton: {
    backgroundColor: '#EF5350',
    color: 'white'
  },
  deleteButton: {
    backgroundColor: '#9E9E9E',
    color: 'white'
  },
  disabledButton: {
    backgroundColor: '#bdbdbd',
    cursor: 'not-allowed',
    opacity: 0.6
  },
  statusSelect: {
    flex: 1,
    padding: '10px',
    fontSize: '14px',
    border: '2px solid #e0e0e0',
    borderRadius: '5px',
    backgroundColor: 'white',
    cursor: 'pointer'
  },
  noAccess: {
    textAlign: 'center',
    padding: '60px 20px',
    backgroundColor: 'white',
    borderRadius: '10px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
  },
  addButton: {
    padding: '10px 20px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer'
  },
  addStairForm: {
    backgroundColor: '#f8f9fa',
    padding: '20px',
    borderRadius: '10px',
    marginBottom: '20px',
    border: '2px solid #4CAF50'
  },
  formTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: '15px'
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '10px',
    marginBottom: '15px'
  },
  input: {
    padding: '10px',
    fontSize: '14px',
    border: '2px solid #e0e0e0',
    borderRadius: '5px',
    boxSizing: 'border-box'
  },
  formActions: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'flex-end'
  },
  cancelButton: {
    padding: '10px 20px',
    backgroundColor: '#9e9e9e',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer'
  },
  submitButton: {
    padding: '10px 20px',
    backgroundColor: '#4CAF50',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer'
  },
  stairSteps: {
    padding: '6px 12px',
    backgroundColor: '#4CAF50',
    color: 'white',
    borderRadius: '15px',
    fontSize: '14px',
    fontWeight: 'bold'
  },
  editButton: {
    backgroundColor: '#2196F3',
    color: 'white'
  },
  editStairForm: {
    backgroundColor: '#e3f2fd',
    padding: '20px',
    borderRadius: '10px',
    marginBottom: '20px',
    border: '2px solid #2196F3'
  },
  updateButton: {
    padding: '10px 20px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer'
  }
};

export default AdminPanel;